import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { changePassword as changePasswordRequest } from '../services/auth';
import AnimatedSection from './AnimatedSection';

const inputClass =
  'w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500';

const Card: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8">
    <h2 className="text-white font-medium mb-1">{title}</h2>
    {hint && <p className="text-sm text-muted mb-6">{hint}</p>}
    {!hint && <div className="mb-6" />}
    {children}
  </div>
);

const AdminAccount: React.FC = () => {
  const { admin, saveProfile } = useAuth();

  const [name, setName] = useState(admin?.name || '');
  const [title, setTitle] = useState(admin?.title || '');
  const [phone, setPhone] = useState(admin?.phone || '');
  const [zohoEmail, setZohoEmail] = useState(admin?.zohoEmail || '');
  const [zohoPassword, setZohoPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isSuperAdmin = admin?.role === 'superadmin';

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    try {
      setSavingProfile(true);
      await saveProfile({
        name,
        title,
        phone,
        zohoEmail,
        // Only sent when they actually typed one — blank leaves the stored one alone.
        ...(zohoPassword ? { zohoPassword } : {}),
      });
      setZohoPassword('');
      setNotice('Your details are saved. The email tools will use them automatically.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your details');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleForgetMailPassword = async () => {
    setError(null);
    setNotice(null);
    try {
      await saveProfile({ clearZohoPassword: true });
      setNotice('Saved mail password removed. You will be asked for it when sending.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the password');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (newPassword !== confirmPassword) {
      setError('The new passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('The new password must be at least 8 characters.');
      return;
    }

    try {
      setChangingPassword(true);
      await changePasswordRequest(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNotice('Your sign-in password has been changed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold text-white mb-2">
                Account
              </h1>
              <p className="text-muted text-sm">
                Signed in as {admin?.email}
                {isSuperAdmin && (
                  <span className="ml-2 px-2 py-0.5 rounded text-xs border border-primary-500/40 bg-primary-500/10 text-primary-400">
                    super admin
                  </span>
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>

        {admin?.mustChangePassword && (
          <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-3 text-sm">
            This account still uses the password it was created with. Change it below.
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="mb-6 rounded-lg border border-primary-500/40 bg-primary-500/10 text-primary-400 px-4 py-3 text-sm">
            {notice}
          </div>
        )}

        <div className="space-y-6">
          {/* Sending details */}
          <AnimatedSection animation="slide-up">
            <Card
              title="Sending details"
              hint="Saved once and reused by both email tools, so you don't retype them each send."
            >
              <form onSubmit={handleSaveProfile} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted mb-2">Your name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Precious"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted mb-2">Your title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Marketing Executive"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted mb-2">Phone number</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+234 ..."
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted mb-2">Zoho email</label>
                    <input
                      type="email"
                      value={zohoEmail}
                      onChange={(e) => setZohoEmail(e.target.value)}
                      placeholder="you@nexasolutra.com"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-surface-border bg-surface p-4">
                  <label className="block text-sm text-muted mb-2">
                    Zoho mail password
                    {admin?.hasSavedMailPassword && (
                      <span className="ml-2 text-primary-400 text-xs">saved</span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={zohoPassword}
                    onChange={(e) => setZohoPassword(e.target.value)}
                    placeholder={
                      admin?.hasSavedMailPassword
                        ? 'Leave blank to keep the saved one'
                        : 'Password or app password'
                    }
                    autoComplete="new-password"
                    className={inputClass}
                  />
                  <p className="text-xs text-muted mt-2">
                    Stored encrypted on the server and used only to send mail as you. It is never
                    sent back to the browser.
                  </p>
                  {admin?.hasSavedMailPassword && (
                    <button
                      type="button"
                      onClick={handleForgetMailPassword}
                      className="text-xs text-muted hover:text-white mt-3"
                    >
                      Forget saved mail password
                    </button>
                  )}
                </div>

                <button type="submit" disabled={savingProfile} className="btn-primary disabled:opacity-60">
                  {savingProfile ? 'Saving…' : 'Save details'}
                </button>
              </form>
            </Card>
          </AnimatedSection>

          {/* Sign-in password */}
          <AnimatedSection animation="slide-up" delay={60}>
            <Card title="Sign-in password" hint="The password you use to get into this admin area.">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm text-muted mb-2">Current password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted mb-2">New password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted mb-2">Confirm new password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className={inputClass}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="btn-primary disabled:opacity-60"
                >
                  {changingPassword ? 'Updating…' : 'Change password'}
                </button>
              </form>
            </Card>
          </AnimatedSection>

        </div>
      </div>
    </section>
  );
};

export default AdminAccount;
